import IconComponent from "@mdi/react";

import {
  mdiAlertCircleOutline,
  mdiCursorDefaultClickOutline,
  mdiDatabaseOffOutline,
  mdiLoading
} from "@mdi/js";
import styled from "styled-components";

import {
  EmptyDetail,
  EmptyState,
  EmptyTitle,
  SkeletonBar,
  TEAL,
  TEAL_DARK,
  spin
} from "./styles";

export function NoSelectionState(): JSX.Element {
  return (
    <EmptyState data-testid="well-card-panel-no-selection">
      <IconComponent path={mdiCursorDefaultClickOutline} size={1.67} color="#c0ccda" />
      <NoSelectionTitle>No well loaded</NoSelectionTitle>
      <NoSelectionDetail>
        Click a well on the map, then choose View Well Card in the popup. Once a well is
        loaded, clicking a row in the well list or the data table switches the card to
        that well.
      </NoSelectionDetail>
    </EmptyState>
  );
}

export interface PanelErrorStateProps {
  title: string;
  detail: string;
  onRetry: () => void;
  /** Sits in the chart's place rather than filling the tab body. */
  compact?: boolean;
  testId?: string;
}

/**
 * A failed fetch renders here rather than as a dash or an empty state — the
 * two look the same to a reader, and only one of them is worth retrying.
 */
export function PanelErrorState({
  title,
  detail,
  onRetry,
  compact = false,
  testId = "well-card-panel-error"
}: PanelErrorStateProps): JSX.Element {
  const Box = compact ? CompactErrorBox : ErrorBox;
  return (
    <Box data-testid={testId}>
      <IconComponent path={mdiAlertCircleOutline} size={1.5} color="#d64550" />
      <EmptyTitle>{title}</EmptyTitle>
      <EmptyDetail>{detail}</EmptyDetail>
      <RetryButton type="button" onClick={onRetry} data-testid={`${testId}-retry`}>
        Try again
      </RetryButton>
    </Box>
  );
}

export function NoProductionState(): JSX.Element {
  return (
    <NoProductionBox data-testid="well-card-panel-no-production">
      <IconComponent path={mdiDatabaseOffOutline} size={1.5} color="#c0ccda" />
      <EmptyTitle>No production reported for this well</EmptyTitle>
      <EmptyDetail>
        The Production Array and the Performance table are hidden until production is
        reported.
      </EmptyDetail>
    </NoProductionBox>
  );
}

/**
 * In the Production Array's place when the chart is normalized and the well
 * has no value to divide by. Its raw numbers under a normalized header would
 * read as normalized.
 */
export function NotNormalizableState(): JSX.Element {
  return (
    <NotNormalizableBox data-testid="well-card-panel-not-normalizable">
      <IconComponent path={mdiDatabaseOffOutline} size={1.5} color="#c0ccda" />
      <EmptyTitle>This well cannot be normalized</EmptyTitle>
      <EmptyDetail>
        It has no value for the chart&apos;s normalize field. Turn normalization off to
        see its Production Array.
      </EmptyDetail>
    </NotNormalizableBox>
  );
}

/** Shown while the panel is deriving its own section line. */
export function ViewerSpinner(): JSX.Element {
  return (
    <SpinnerBox data-testid="well-card-panel-deriving">
      <Spinner path={mdiLoading} size={1.25} color={TEAL} />
    </SpinnerBox>
  );
}

export function HeaderSkeleton(): JSX.Element {
  return (
    <HeaderSkeletonWrapper>
      <SkeletonBar $width="280px" $height={20} />
      <SkeletonRow>
        <SkeletonBar $width="150px" />
        <SkeletonBar $width="190px" />
        <SkeletonBar $width="110px" />
        <SkeletonBar $width="130px" />
      </SkeletonRow>
    </HeaderSkeletonWrapper>
  );
}

/**
 * Stands in for the chart while its own fetch runs. Only the chart waits on it,
 * so the header and the field-backed blocks stay on screen — a tab switch is
 * not a reason to blank the well's name.
 */
export function ChartSkeleton(): JSX.Element {
  return (
    <ChartSkeletonWrapper data-testid="well-card-panel-production-loading">
      <PlotSkeleton>
        <Spinner path={mdiLoading} size={1.25} color={TEAL} />
      </PlotSkeleton>
    </ChartSkeletonWrapper>
  );
}

export function BodySkeleton(): JSX.Element {
  return (
    <BodySkeletonWrapper data-testid="well-card-panel-loading">
      <ToolbarSkeleton>
        <SkeletonBar $width="210px" $height={14} />
        <Spacer />
        <SkeletonBar $width="230px" $height={14} />
      </ToolbarSkeleton>
      <PlotSkeleton>
        <Spinner path={mdiLoading} size={1.25} color={TEAL} />
      </PlotSkeleton>
      <SkeletonBar $width="130px" $height={11} />
      <BlockGrid>
        {[0, 1, 2, 3, 4].map((i) => (
          <BlockSkeleton key={i} />
        ))}
      </BlockGrid>
      <SkeletonBar $width="160px" $height={11} />
      <TallBlockSkeleton />
    </BodySkeletonWrapper>
  );
}

const SpinnerBox = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoSelectionTitle = styled(EmptyTitle)`
  font-size: 15px;
`;

const NoSelectionDetail = styled(EmptyDetail)`
  font-size: 12.5px;
  max-width: 340px;
`;

const NotNormalizableBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 24px 14px;
`;

const NoProductionBox = styled.div`
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 12px 14px;
`;

const ErrorBox = styled(EmptyState)`
  background: #fdf3f4;
  border-top: 1px solid #f4d4d7;
`;

const CompactErrorBox = styled(NoProductionBox)`
  background: #fdf3f4;
  border: 1px solid #f4d4d7;
  border-radius: 4px;
`;

const RetryButton = styled.button`
  height: 26px;
  padding: 0 14px;
  border: 1px solid #d9e1e2;
  border-radius: 4px;
  background: #fff;
  color: #3c4858;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    border-color: ${TEAL};
    color: ${TEAL_DARK};
  }
`;

const HeaderSkeletonWrapper = styled.div`
  padding: 12px 14px 10px;
  border-bottom: 1px solid #e3e6e8;
  flex: none;
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 22px;
  margin-top: 9px;
`;

const BodySkeletonWrapper = styled.div`
  padding: 0 14px 14px;
`;

const ChartSkeletonWrapper = styled.div`
  flex: none;
  padding: 0 14px;
`;

const ToolbarSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  margin: 0 -14px;
  padding: 0 12px;
  border-bottom: 1px solid #eef1f6;
`;

const Spacer = styled.span`
  flex: 1;
`;

const PlotSkeleton = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eef1f6;
  border-radius: 4px;
  margin: 12px 0 18px;
`;

const Spinner = styled(IconComponent)`
  animation: ${spin} 0.9s linear infinite;
`;

const BlockGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px 18px;
  margin: 10px 0 20px;
`;

const BlockSkeleton = styled(SkeletonBar).attrs({ $height: 34 })`
  background: #f4f6f9;
`;

const TallBlockSkeleton = styled(SkeletonBar).attrs({ $height: 120 })`
  background: #f4f6f9;
  margin-top: 10px;
`;
